import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, TextField, MenuItem } from "@mui/material";
import { typo } from "../../../../assets/fonts/font";
import colorConfigs from "../../../../configs/colorConfigs";
import PhoneInput from "react-phone-input-2";
import { useCart } from "../../../../context/CartContext";
import { IDeliveryDetails } from "../../../../models/IWallet";

const ShippingInformationForm = () => {
    const { deliveryDetails, setDeliveryDetails } = useCart();
    const [phone, setPhone] = useState("");
    const [countryCode, setCountryCode] = useState("");
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (!deliveryDetails) return;
        
        setDeliveryDetails((prev: IDeliveryDetails) => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePhoneChange = (value: string, data: any) => {
        const phoneNumber = value.slice(data.dialCode.length);
        setPhone(phoneNumber);
        setCountryCode(`+${data.dialCode}`);
        
        if (!deliveryDetails) return;
        
        setDeliveryDetails((prev: IDeliveryDetails) => ({
            ...prev,
            shipping_phone_number: `+${data.dialCode}${phoneNumber}`
        }));
    };

    // Initialize phone number from deliveryDetails
    useEffect(() => {
        if (!deliveryDetails?.shipping_phone_number) return;
        
        const fullPhone = deliveryDetails.shipping_phone_number;
        // Extract country code (assuming format like +65XXXXXXXX)
        const codeMatch = fullPhone.match(/^\+\d+/);
        if (codeMatch) {
            setCountryCode(codeMatch[0]);
            setPhone(fullPhone.slice(codeMatch[0].length));
        } else {
            setPhone(fullPhone);
        }
    }, [deliveryDetails?.shipping_phone_number]);

    if (!deliveryDetails) {
        return null; // or some loading state
    }

    return (
        <Box
            sx={{
                padding: "1.5rem",
                border: "1px solid var(--color-black)",
                borderRadius: "1rem",
                backgroundColor: "var(--color-white)",
                mr: { md: 2, xs: 0 },
            }}
        >
            <Typography sx={{ ...typo.title, mb: 2 }}>Delivery</Typography>

            <Grid container spacing={2}>
                {/* Country/Region */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ ...typo.smallText }}>Country/Region</Typography>
                    <TextField
                        select
                        fullWidth
                        value="Singapore"
                        disabled
                        size="small"
                        sx={{ ...typo.smallText }}
                    >
                        <MenuItem value="Singapore">
                            <Typography sx={{ ...typo.smallText }}>Singapore</Typography>
                        </MenuItem>
                    </TextField>
                </Grid>

                {/* First name & Last name side-by-side */}
                <Grid item xs={6}>
                    <Typography variant="subtitle1" sx={{ ...typo.smallText }}>First name</Typography>
                    <TextField
                        fullWidth
                        name="shipping_first_name"
                        value={deliveryDetails.shipping_first_name || ''}
                        onChange={handleInputChange}
                        required
                        size="small"
                        InputLabelProps={{ sx: { ...typo.bodyContent } }}
                        InputProps={{
                            inputProps: { style: { ...typo.bodyContent } },
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                    borderColor: colorConfigs.primary,
                                    ...typo.bodyContent,
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": { ...typo.bodyContent },
                        }}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Typography variant="subtitle1" sx={{ ...typo.smallText }}>Last name</Typography>
                    <TextField
                        fullWidth
                        name="shipping_last_name"
                        value={deliveryDetails.shipping_last_name || ''}
                        onChange={handleInputChange}
                        required
                        size="small"
                        InputLabelProps={{ sx: { ...typo.bodyContent } }}
                        InputProps={{
                            inputProps: { style: { ...typo.bodyContent } },
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                    borderColor: colorConfigs.primary,
                                    ...typo.bodyContent,
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": { ...typo.bodyContent },
                        }}
                    />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ ...typo.smallText }}>Address</Typography>
                    <TextField
                        fullWidth
                        name="shipping_address"
                        value={deliveryDetails.shipping_address || ''}
                        onChange={handleInputChange}
                        required
                        size="small"
                        InputLabelProps={{ sx: { ...typo.bodyContent } }}
                        InputProps={{
                            inputProps: { style: { ...typo.bodyContent } },
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                    borderColor: colorConfigs.primary,
                                    ...typo.bodyContent,
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": { ...typo.bodyContent },
                        }}
                    />
                </Grid>

                {/* Postal code */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ ...typo.smallText }}>Postal code</Typography>
                    <TextField
                        fullWidth
                        name="shipping_postal_code"
                        value={deliveryDetails.shipping_postal_code || ''}
                        onChange={handleInputChange}
                        required
                        size="small"
                        InputLabelProps={{ sx: { ...typo.bodyContent } }}
                        InputProps={{
                            inputProps: { style: { ...typo.bodyContent } },
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": {
                                    borderColor: colorConfigs.primary,
                                    ...typo.bodyContent,
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": { ...typo.bodyContent },
                        }}
                    />
                </Grid>

                {/* Phone */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ ...typo.smallText }}>Phone</Typography>
                    <Box sx={{
                        '& .react-tel-input': {
                            fontFamily: typo.bodyContent.fontFamily,
                            fontSize: typo.bodyContent.fontSize,
                            '& .form-control': {
                                height: '40px',
                                width: '100%',
                                fontSize: typo.bodyContent.fontSize,
                                paddingLeft: '48px',
                                borderRadius: '4px',
                                borderColor: 'var(--color-overlay-black-23)',
                                '&:hover': {
                                    borderColor: colorConfigs.primary,
                                },
                                '&:focus': {
                                    borderColor: colorConfigs.primary,
                                    boxShadow: `0 0 0 1px ${colorConfigs.primary}`,
                                }
                            },
                            '& .flag-dropdown': {
                                borderRadius: '4px 0 0 4px',
                                '&:hover, &:focus': {
                                    backgroundColor: 'var(--color-transparent)',
                                }
                            }
                        }
                    }}>
                        <PhoneInput
                            country={"sg"}
                            value={countryCode + phone}
                            onChange={handlePhoneChange}
                            inputProps={{
                                name: "shipping_phone_number",
                                required: true,
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ShippingInformationForm;